import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, PackageCheck, RefreshCw } from "lucide-react";
import api from "../lib/api";
import { formatDay } from "../lib/finance";
import { ConditionText, LoanNote, StatusBadge, WarrantyNote } from "../components/procurement/ProcurementBits";
import { procurementError, typeLabel } from "../lib/procurement";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Skeleton } from "../components/ui/skeleton";

// « Mon matériel »: what is in the hands of the signed-in person's own collaborator fiche, nothing else (the holder relation, D-62).
// No cost, funder, serial number or journal here: the server never sends them to a holder.
export default function ProcurementMine() {
  const [data, setData] = useState(null);
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const load = useCallback(async () => {
    setPhase("loading");
    try {
      const { data: body } = await api.get("/procurement/assets/mine");
      setData(body);
      setPhase("ready");
    } catch (e) {
      setError(procurementError(e, "Impossible de charger votre matériel."));
      setPhase("error");
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4" data-testid="procurement-mine-page">
      <div className="min-w-0">
        <h1 className="font-display font-bold text-2xl text-slate-900">Mon matériel</h1>
        <p className="text-sm text-slate-500">Ce que l'entreprise vous a confié, avec l'état et la garantie.</p>
      </div>
      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-20 w-full" /><Skeleton className="h-20 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="mine-empty">
          <PackageCheck className="mx-auto text-slate-300" size={26} />
          <h3 className="font-medium text-slate-900">Aucun matériel ne vous est confié</h3>
          <p className="text-sm text-slate-500 max-w-md mx-auto">
            {data.has_fiche ? "Quand un ordinateur, un téléphone ou une licence vous sera affecté ou prêté, vous le retrouverez ici."
              : "Votre compte n'est pas lié à une fiche collaborateur : le matériel ne peut pas vous être rattaché. Demandez à un administrateur de faire la liaison."}
          </p>
        </Card>
      )}
      {phase === "ready" && data.items.length > 0 && (
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3" data-testid="mine-list">
          {data.items.map((a) => (
            <Card key={a.id} className="p-4 space-y-2" data-testid={`mine-card-${a.number}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0"><div className="font-medium text-slate-900 break-words">{a.label}</div><div className="text-xs text-slate-500 tabular-nums">{a.number} · {typeLabel(a.type)}</div></div>
                <StatusBadge status={a.status} />
              </div>
              <dl className="text-sm text-slate-700 space-y-0.5">
                {a.location && <div>{a.location}</div>}
                <div><ConditionText condition={a.condition} />{a.warranty_until ? ` · garantie jusqu'au ${formatDay(a.warranty_until)}` : ""}</div>
              </dl>
              <div className="flex flex-wrap gap-x-3"><LoanNote asset={a} /><WarrantyNote asset={a} /></div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
